import {Device} from "../Device";
import {MobileView} from "../MobileView";
import {DeviceOrientation} from "../DeviceOrientation";

export class Button extends MobileView {
    static readonly LANDSCAPE_TEXTURES: string = "landscapeTextures";
    static readonly PORTRAIT_TEXTURES: string = "portraitTextures";
    static readonly STATE_UP: string = "up";
    static readonly STATE_DOWN: string = "down";
    static readonly STATE_OVER: string = "over";
    static readonly STATE_DISABLED: string = "disabled";

    protected _isDown: boolean = false;
    protected _isOver: boolean = false;
    protected _background: PIXI.Sprite;
    protected _textures: any;
    protected _state: string;
    protected _hasTexturesBasedOnOrientation: boolean
    protected _scaleTween: gsap.TweenMax;
    protected _baseScale: number = 1

    scaleTimeOnMouseOver: number = 0.3
    scaleOnMouseOver: number = 1.1
    shouldScaleOnMouseOver: boolean = false
    scaleTimeOnMouseDown: number = 0.3
    scaleOnMouseDown: number = 1.1
    shouldScaleOnMouseDown: boolean = false

    constructor(textures: any) {
        super();

        this.textures = textures;

        this.on(Device.resolveEvent("mousedown"), this.onMouseDown, this);
        this.on(Device.resolveEvent("mouseup"), this.onMouseUp, this);
        this.on(Device.resolveEvent("mouseupoutside"), this.onMouseUp, this);
        this.on(Device.resolveEvent("mouseover"), this.onMouseOver, this);
        this.on(Device.resolveEvent("mouseout"), this.onMouseOut, this);

        this._background = new PIXI.Sprite(new PIXI.Texture(new PIXI.BaseTexture(null)));
        this.addChild(this._background);

        this.interactive = true;
        this.buttonMode = true;

        this.setImageForState(Button.STATE_UP);
    }

    get baseScale(): number {
        return this._baseScale
    }

    set baseScale(value: number) {
        this._baseScale = value

        if (!this._isOver || !this.shouldScaleOnMouseOver || !this._isDown || !this.shouldScaleOnMouseDown) {
            this.scale.set(this._baseScale, this._baseScale)
        }
    }

    refreshState() {
        this.setImageForState(this._state);
    }

    resetScale() {
        if (this._scaleTween) {
            this._scaleTween.kill();
        }

        this.scale.set(this._baseScale)
    }

    dispose() {
        this.off(Device.resolveEvent("mousedown"), this.onMouseDown, this);
        this.off(Device.resolveEvent("mouseup"), this.onMouseUp, this);
        this.off(Device.resolveEvent("mouseupoutside"), this.onMouseUp, this);
        this.off(Device.resolveEvent("mouseover"), this.onMouseOver, this);
        this.off(Device.resolveEvent("mouseout"), this.onMouseOut, this);

        if (this._scaleTween) {
            this._scaleTween.kill();
        }

        //this.destroy();
    }

    set textures(value: any) {
        this._hasTexturesBasedOnOrientation = value[Button.LANDSCAPE_TEXTURES] != null || value[Button.PORTRAIT_TEXTURES] != null;

        if (value[Button.LANDSCAPE_TEXTURES] != null && value[Button.LANDSCAPE_TEXTURES][Button.STATE_UP] == null) {
            throw "At least a 'state up' should be supplied for landscape orientation";
        }

        if (value[Button.PORTRAIT_TEXTURES] != null && value[Button.PORTRAIT_TEXTURES][Button.STATE_UP] == null) {
            throw "At least a 'state up' should be supplied for portrait orientation";
        }

        if (!this._hasTexturesBasedOnOrientation && value[Button.STATE_UP] == null) {
            throw "At least a 'state up' should be supplied";
        }

        this._textures = value;
    }

    protected setImageForState(state: string) {
        var textures = null;

        if (this._hasTexturesBasedOnOrientation) {
            if (MobileView.deviceOrientation == DeviceOrientation.Landscape) {
                textures = this._textures[Button.LANDSCAPE_TEXTURES] || this._textures[Button.PORTRAIT_TEXTURES]
            } else {
                textures = this._textures[Button.PORTRAIT_TEXTURES] || this._textures[Button.LANDSCAPE_TEXTURES]
            }
        } else {
            textures = this._textures
        }

        if (textures[state] === undefined) {
            state = Button.STATE_UP;
        }

        // TODO: check this behavior
        if (!this.interactive && textures[Button.STATE_DISABLED] !== undefined)
            state = Button.STATE_DISABLED;
        else if (this.interactive && this._state == Button.STATE_DISABLED)
            state = Button.STATE_UP;
        // else if(!this.interactive && this.textures[Button.STATE_DISABLED] === undefined)
        //     state = Button.STATE_UP;

        this._state = state;

        this._background.texture = textures[state];
    }

    protected onMouseDown() {
        this._isDown = true;
        this.setImageForState(Button.STATE_DOWN);

        if (this.shouldScaleOnMouseDown) {
            this._scaleTween = gsap.TweenMax.to(this.scale, this.scaleTimeOnMouseDown, {
                x: this._baseScale * this.scaleOnMouseDown,
                y: this._baseScale * this.scaleOnMouseDown
            });
        }
    }

    protected onMouseUp() {
        this._isDown = false;

        if (this._isOver) {
            this.setImageForState(Button.STATE_OVER);
        } else {
            this.setImageForState(Button.STATE_UP);
        }

        if (this._isOver && this.shouldScaleOnMouseOver) {
            this._scaleTween = gsap.TweenMax.to(this.scale, this.scaleTimeOnMouseDown, {
                x: this._baseScale * this.scaleOnMouseOver,
                y: this._baseScale * this.scaleOnMouseOver
            });
        } else {
            this._scaleTween = gsap.TweenMax.to(this.scale, this.scaleTimeOnMouseDown,
                {x: this._baseScale, y: this._baseScale});
        }
    }

    protected onMouseOver() {
        this._isOver = true;
        if (!this._isDown) {
            this.setImageForState(Button.STATE_OVER);
        }

        if (this.shouldScaleOnMouseOver) {
            this._scaleTween = gsap.TweenMax.to(this.scale, this.scaleTimeOnMouseOver, {
                x: this._baseScale * this.scaleOnMouseOver,
                y: this._baseScale * this.scaleOnMouseOver
            });
        }
    }

    protected onMouseOut() {
        this._isOver = false;
        if (!this._isDown) {
            this.setImageForState(Button.STATE_UP);
        }

        if (this.shouldScaleOnMouseOver && this.scale.x != this._baseScale) {
            this._scaleTween = gsap.TweenMax.to(this.scale, this.scaleTimeOnMouseOver, {
                x: this._baseScale,
                y: this._baseScale
            });
        }
    }

    protected onRotationChange(): void {
        this.setImageForState(this._state)
    }
}
